import { assignBlockIds, createSeededBlockIdGenerator } from "./block-id";
import { CURRENT_SCHEMA_VERSION, DOCUMENT_FORMAT, type SerializedNodeLike } from "./types";
import type { DocumentIssue } from "./validate";

/** Upgrades a document from `fromVersion` to `fromVersion + 1`. Must not mutate its input. */
export type Migration = (document: Record<string, unknown>) => Record<string, unknown>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Gives every block that lacks a persistent ID one. IDs are derived from the document ID and the
 * block's position, so everyone who reads the same stored bytes (two tabs, a server render) agrees
 * on them before any of them has saved. Also run on current-version documents when they are
 * opened, because hosts may build content by hand. Returns its input when nothing is missing.
 */
export function identifyBlocks(document: Record<string, unknown>): Record<string, unknown> {
  const content = document.content;
  if (!isRecord(content) || !isRecord(content.root)) return document;
  const seed = typeof document.documentId === "string" ? document.documentId : "";
  const { root, assigned } = assignBlockIds(
    content.root as SerializedNodeLike,
    createSeededBlockIdGenerator(seed),
  );
  return assigned === 0 ? document : { ...document, content: { ...content, root } };
}

/**
 * Keyed by the version a migration upgrades *from*. The schema version is ours: a Lexical upgrade
 * never changes it by itself.
 *
 * - 1 → 2: blocks gain persistent IDs.
 */
export const MIGRATIONS: Readonly<Record<number, Migration>> = { 1: identifyBlocks };

export type MigrationResult =
  | { ok: true; document: Record<string, unknown>; migrated: boolean }
  | { ok: false; issue: DocumentIssue };

export function migrateDocument(
  input: unknown,
  migrations: Readonly<Record<number, Migration>> = MIGRATIONS,
  targetVersion: number = CURRENT_SCHEMA_VERSION,
): MigrationResult {
  if (
    typeof input !== "object" ||
    input === null ||
    Array.isArray(input) ||
    (input as Record<string, unknown>).format !== DOCUMENT_FORMAT
  ) {
    return {
      ok: false,
      issue: { code: "not-a-document", message: `Not a ${DOCUMENT_FORMAT} document.` },
    };
  }
  let document = input as Record<string, unknown>;
  const startVersion = document.schemaVersion;
  if (typeof startVersion !== "number" || !Number.isInteger(startVersion) || startVersion < 1) {
    return { ok: false, issue: { code: "invalid-envelope", message: "schemaVersion is missing." } };
  }
  if (startVersion > targetVersion) {
    return {
      ok: false,
      issue: {
        code: "future-version",
        message: `Written by a newer editor (schema ${startVersion}; this one reads ${targetVersion}).`,
      },
    };
  }
  for (let version = startVersion; version < targetVersion; version++) {
    const migrate = migrations[version];
    if (migrate === undefined) {
      return {
        ok: false,
        issue: { code: "migration-failed", message: `No migration from schema ${version}.` },
      };
    }
    try {
      document = { ...migrate(document), schemaVersion: version + 1 };
    } catch (error) {
      return {
        ok: false,
        issue: {
          code: "migration-failed",
          message: `Migration from schema ${version} threw: ${error instanceof Error ? error.message : String(error)}`,
        },
      };
    }
  }
  return { ok: true, document, migrated: startVersion !== targetVersion };
}
